import React, { useEffect, useState } from "react";
import axios from "../api/axios";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Chip,
  Stack,
  Divider,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { useMessage } from "../context/MessageContext";
import { useConfirm } from "../context/ConfirmContext";

const SectionEditor = ({ label, items, onAdd, onDelete, input, setInput }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle1" fontWeight="bold">
      {label}
    </Typography>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <TextField
        value={input}
        onChange={(e) => setInput(e.target.value)}
        size="small"
        placeholder={`Enter ${label.slice(0, -1)}`}
        sx={{ width: 200 }}
      />
      <Button
        onClick={onAdd}
        variant="contained"
        size="small"
        disabled={!input}
      >
        Add
      </Button>
    </Stack>
    <Stack direction="row" spacing={1} flexWrap="wrap">
      {items.map((item, i) => (
        <Chip
          key={i}
          label={item}
          onDelete={() => onDelete(i)}
          color="primary"
          sx={{ mb: 1 }}
        />
      ))}
    </Stack>
  </Box>
);

const Masters = () => {
  const { showMessage } = useMessage();
  const { confirm } = useConfirm();

  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [masters, setMasters] = useState({
    grades: [],
    programs: [],
    subjects: [],
  });
  const [inputs, setInputs] = useState({ grade: "", program: "", subject: "" });
  const [loading, setLoading] = useState(true);

  const fetchOrgs = async () => {
    try {
      const res = await axios.get("/orgs");
      setOrgs(res.data);
      if (res.data[0]) setSelectedOrg(res.data[0]._id);
    } catch (err) {
      console.error(err);
      showMessage("Failed to load organizations", "error");
    }
  };

  const fetchMasters = async (orgId) => {
    if (!orgId) return;
    try {
      setLoading(true);
      const res = await axios.get(`/masters/${orgId}`);
      setMasters(res.data);
    } catch (err) {
      console.error(err);
      showMessage("Failed to load master data", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgs();
  }, []);

  useEffect(() => {
    if (selectedOrg) fetchMasters(selectedOrg);
  }, [selectedOrg]);

  const saveMasters = async (updated) => {
    try {
      const res = await axios.put(`/masters/${selectedOrg}`, updated);
      setMasters(res.data);
      showMessage("Master data updated", "success");
    } catch (err) {
      console.error(err);
      showMessage("Failed to save master data", "error");
    }
  };

  const handleAdd = (type) => {
    const updated = {
      ...masters,
      [type + "s"]: [...masters[type + "s"], inputs[type]],
    };
    setInputs({ ...inputs, [type]: "" });
    saveMasters(updated);
  };

  const handleDelete = async (type, idx) => {
    const confirmed = await confirm({
      title: `Delete ${type}`,
      message: `Are you sure you want to delete this ${type}?`,
    });
    if (confirmed) {
      const updated = {
        ...masters,
        [type + "s"]: masters[type + "s"].filter((_, i) => i !== idx),
      };
      saveMasters(updated);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Masters (Grades / Programs / Subjects)
      </Typography>
      <FormControl sx={{ mb: 2, minWidth: 240 }}>
        <InputLabel>Organization</InputLabel>
        <Select
          value={selectedOrg}
          onChange={(e) => setSelectedOrg(e.target.value)}
          label="Organization"
        >
          {orgs.map((org) => (
            <MenuItem key={org._id} value={org._id}>
              {org.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Paper sx={{ p: 3 }}>
        <SectionEditor
          label="Grades"
          items={masters.grades || []}
          input={inputs.grade}
          setInput={(val) => setInputs({ ...inputs, grade: val })}
          onAdd={() => handleAdd("grade")}
          onDelete={(idx) => handleDelete("grade", idx)}
        />
        <Divider sx={{ my: 2 }} />
        <SectionEditor
          label="Programs"
          items={masters.programs || []}
          input={inputs.program}
          setInput={(val) => setInputs({ ...inputs, program: val })}
          onAdd={() => handleAdd("program")}
          onDelete={(idx) => handleDelete("program", idx)}
        />
        <Divider sx={{ my: 2 }} />
        <SectionEditor
          label="Subjects"
          items={masters.subjects || []}
          input={inputs.subject}
          setInput={(val) => setInputs({ ...inputs, subject: val })}
          onAdd={() => handleAdd("subject")}
          onDelete={(idx) => handleDelete("subject", idx)}
        />
      </Paper>
    </Box>
  );
};

export default Masters;
